import React, { useCallback, useMemo } from 'react'
import { NodeToolbar } from '@xyflow/react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import { useEntityGraphContext } from '../../Wrapper/EntityGraphProvider'
import Icon from '../../../../atoms/Icon'

const useStyles = makeStyles({
  toolbarContainer: {
    display: 'flex',
    backgroundColor: 'var(--color-primary-400)',
    color: 'var(--color-contrast-primary-400)',
    overflow: 'hidden',
    borderRadius: 'var(--border-radius-sm)',
    '& button': {
      display: 'flex',
      padding: 'var(--padding-sm)',
      '&.active': {
        backgroundColor: 'var(--color-primary-500)',
        color: 'var(--color-contrast-primary-500)'
      }
    }
  }
})

export const withNodeDefaults = (NodeComponent) => {
  const WrappedNode = (node) => {
    const classes = useStyles()
    const {
      state: { config, styleEditItem, linkEditItem, detailsItem },
      dispatch
    } = useEntityGraphContext()

    const toolbarPosition = useMemo(
      () => node?.nodeProps?.toolbarPosition ?? config?.canvasSettings?.toolbarPosition ?? 'top',
      [node, config]
    )
    const toolbarVisible = useMemo(() => !!node?.selected && config.canvasSettings.showToolbar, [config.canvasSettings.showToolbar, node?.selected])

    const onClickToggle = useCallback((path) => {
      dispatch({
        type: 'TOGGLE_EDIT_SIDEBAR',
        payload: {
          path: path,
          value: {
            id: node.id,
            type: 'node'
          }
        }
      })
    }, [dispatch, node])

    return (
      <>
        <NodeToolbar isVisible={toolbarVisible} position={toolbarPosition}>
          <div className={classes.toolbarContainer}>
            <button
              onClick={() => onClickToggle('styleEditItem')}
              className={clsx({
                active: styleEditItem?.id === node.id && styleEditItem?.type === 'node'
              })}
              title='Edit Node Styles'
            >
              <Icon name='edit' />
            </button>
            <button
              onClick={() => onClickToggle('linkEditItem')}
              className={clsx({
                active: linkEditItem?.id === node.id && linkEditItem?.type === 'node'
              })}
              title='Link Entity Points'
            >
              <Icon name='link' />
            </button>
            {!!node?.data?.entityProps && (
              <button
                onClick={() => onClickToggle('detailsItem')}
                className={clsx({
                  active: detailsItem?.id === node.id && detailsItem?.type === 'node'
                })}
                title='View Details'
              >
                <Icon name='listAlt' />
              </button>
            )}
          </div>
        </NodeToolbar>
        <NodeComponent {...node} />
      </>
    )
  }

  WrappedNode.propTypes = {
    nodeProps: PropTypes.shape({
      toolbarPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
      toolbarVisible: PropTypes.bool
    })
  }

  return WrappedNode
}
